import { getResellBuybacks } from 'lib/api/reSell/factoring';
import { ResellBuyback } from 'lib/api/reSell/factoring/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { AutocompleteOption } from 'lib/common/GlobalSearcher/filterTypes';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import RevertBuybackDialog from './components/RevertBuybackDialog';

const ResellBuybacks: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [buybacks, setBuybacks] = useState<ResellBuyback[]>([]);
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [selectedBuyback, setSelectedBuyback] = useState<ResellBuyback | null>(
    null,
  );
  const [searchResellId, setSearchResellId] = useState('');
  const [searchFolio, setSearchFolio] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [selectedFund, setSelectedFund] = useState<AutocompleteOption | null>(
    null,
  );
  const [selectedSortBy, setSelectedSortBy] = useState<string>('buybackAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    if (searchResellId) query += `resellId=${searchResellId}&`;
    if (searchFolio) query += `folio=${searchFolio}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (fundId) query += `fundId=${fundId}&`;
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (exportComplete) query += `all=true&`;
    return query;
  };

  const fetchBuybacks = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize, exportComplete });
      const response = await getResellBuybacks(dispatch, query);
      if (!exportComplete) {
        setBuybacks(response?.data || []);
        setPagination(
          response?.pagination || {
            page: 1,
            pageSize: 10,
            total: 0,
            totalPages: 0,
          },
        );
      }
      setLoading(false);
      return response;
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener las recompras');
      setLoading(false);
      return undefined;
    }
  };

  const setUp = async () => {
    await fetchBuybacks({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleRevertBuyback = (id: number) => {
    const buyback = buybacks.find((buyback) => buyback.id === id);
    setSelectedBuyback(buyback || null);
    setOpenRevertModal(true);
  };

  const actions = [
    {
      action: handleRevertBuyback,
      actionLabel: 'Reversar',
      canShow: () =>
        validatePermissions(user, 'financing_fundSale_buybacks_delete'),
    },
  ];

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Id venta',
      value: searchResellId,
      setter: setSearchResellId,
    },
    {
      type: 'text',
      label: 'Folio',
      value: searchFolio,
      setter: setSearchFolio,
    },
    {
      type: 'date',
      label: 'Fecha',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
      pairKey: 'endDate',
    },
    {
      type: 'date',
      label: 'Fecha',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
      pairKey: 'startDate',
    },
    {
      type: 'autocomplete',
      label: 'Fondo',
      value: fundId,
      key: 'fundName',
      setter: (value: string | null) => {
        setFundId(value);
      },
      selectedValue: selectedFund,
      setSelectedValue: (value: AutocompleteOption | null) => {
        setSelectedFund(value);
        if (value?.object?.id) {
          setFundId(value.object.id);
        } else {
          setFundId(null);
        }
      },
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás las recompras de ventas a fondos."
        variant="body1"
        buttons={[
          {
            name: 'Buscar',
            color: 'primary',
            action: () => {},
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={() => fetchBuybacks({ page: 1, pageSize: 10 })}
                dispatch={dispatch}
                selectedSortBy={selectedSortBy}
                setSelectedSortBy={setSelectedSortBy}
                sortOptions={[{ value: 'buybackAt', label: 'Fecha recompra' }]}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
              />
            ),
          },
        ]}
      />
      <TableWithPagination
        data={buybacks}
        setData={setBuybacks}
        dataHeaders={[
          { key: 'id', label: 'Id', type: 'numeric-id' },
          { key: 'resellId', label: 'Id venta', type: 'numeric-id' },
          { key: 'folio', label: 'Folio' },
          { key: 'businessName', label: 'Cliente' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'fundName', label: 'Fondo' },
          { key: 'resellAmount', label: 'Monto venta', type: 'money' },
          { key: 'amount', label: 'Monto recompra', type: 'money' },
          { key: 'buybackAt', label: 'Fecha recompra', type: 'date' },
        ]}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchBuybacks}
        actions={actions}
        exportToFile="xlsx"
        exportComplete
        setLoading={setLoading}
      />
      {openRevertModal && selectedBuyback && (
        <RevertBuybackDialog
          open={openRevertModal}
          setOpen={setOpenRevertModal}
          buyback={selectedBuyback}
          setUp={setUp}
        />
      )}
    </div>
  );
};

export default ResellBuybacks;
